import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import NextImage from "next/image";
import { createPortal } from "react-dom";
import { CSSTransition } from "react-transition-group";
import { ModalTriggerButton } from "@components/ui-kit/elements/buttons/modal-trigger-button/modal-trigger-button";
import { HeroCard } from "@components/ui-kit/organisms/cards/hero-card/hero-card";
import { HomeModelItem, resolveResponsivePoint } from "@entities/home/helpers";
import useDataLayerAnalytics from "@hooks/use-analytics";
import { BreakpointValue } from "@models/breakpoints";
import { useHomeItemController } from "@views/home/controllers/use-home-item-controller";
import styles from "@views/home/home.module.scss";
const POPUP_SCALE_TRANSITION_MS = 220;
const HERO_CARD_IMAGE = "/images/large-hero-card-media.png";

/** Размер HeroCard size="large" (из hero-card.module.scss) */
const HERO_CARD_LARGE_SIZE = {
  width: 468,
  height: 468
};
interface HomeItemBlockProps {
  item: HomeModelItem;
  isPriorityImage?: boolean;
  breakpoint: BreakpointValue;
  isCardOpen: boolean;
  isCardVisible: boolean;
  cardPosition: {
    x: number;
    y: number;
  } | null;
  isCenterZoomed?: boolean;
  activeOriginIndex: number;
  isKeyboardActiveItem: boolean;
  keyboardOriginIndex: number;
  onKeyboardNavigateItem: (item: HomeModelItem, originIndex: number) => void;
  onKeyboardStateChange: (itemId: number, originIndex: number) => void;
  onShowCard: (itemId: number, x: number, y: number, originIndex: number) => void;
  onCloseCard: () => void;
}
const HomeItemBlock = t0 => {
  const $ = _c(64);
  const {
    item,
    isPriorityImage: t1,
    breakpoint,
    isCardOpen,
    isCardVisible,
    cardPosition,
    isCenterZoomed: t2,
    activeOriginIndex,
    isKeyboardActiveItem,
    keyboardOriginIndex,
    onKeyboardNavigateItem,
    onKeyboardStateChange,
    onShowCard,
    onCloseCard
  } = t0;
  const isPriorityImage = t1 === undefined ? false : t1;
  const isCenterZoomed = t2 === undefined ? false : t2;
  const popupNodeRef = useRef(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const triggerRefs = useRef(t3);
  const {
    setDataLayerAction,
    setDataLayerProductInMainClick
  } = useDataLayerAnalytics();
  let t4;
  if ($[1] !== activeOriginIndex || $[2] !== breakpoint || $[3] !== isCardOpen || $[4] !== item || $[5] !== onCloseCard || $[6] !== onShowCard) {
    t4 = {
      item,
      breakpoint,
      isCardOpen,
      activeOriginIndex,
      onShowCard,
      onCloseCard
    };
    $[1] = activeOriginIndex;
    $[2] = breakpoint;
    $[3] = isCardOpen;
    $[4] = item;
    $[5] = onCloseCard;
    $[6] = onShowCard;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    activeProductInfo,
    baseSize,
    productInfos,
    size,
    wrapperStyle,
    handleProductCardClick,
    handleTriggerClick,
    handleTriggerEnter,
    handleTriggerHover
  } = useHomeItemController(t4);
  let t5;
  if ($[8] !== isCardOpen || $[9] !== item.productId || $[10] !== setDataLayerAction) {
    t5 = () => {
      if (isCardOpen) {
        setDataLayerAction({
          action: "show",
          eventCategory: "interaction",
          eventLabel: "productInMain",
          eventLocation: "mainPage",
          eventContent: item.productId
        });
      }
    };
    $[8] = isCardOpen;
    $[9] = item.productId;
    $[10] = setDataLayerAction;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== isCardOpen || $[13] !== item || $[14] !== setDataLayerAction) {
    t6 = [isCardOpen, item, setDataLayerAction];
    $[12] = isCardOpen;
    $[13] = item;
    $[14] = setDataLayerAction;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[16] !== isKeyboardActiveItem || $[17] !== item.id || $[18] !== keyboardOriginIndex || $[19] !== onKeyboardStateChange || $[20] !== productInfos.length) {
    t7 = () => {
      if (!isKeyboardActiveItem) {
        return;
      }
      if (productInfos.length === 0) {
        return;
      }
      const safeIndex = Math.min(Math.max(keyboardOriginIndex, 0), productInfos.length - 1);
      if (safeIndex !== keyboardOriginIndex) {
        onKeyboardStateChange(item.id, safeIndex);
        return;
      }
      const frameId = requestAnimationFrame(() => {
        triggerRefs.current[safeIndex]?.focus({
          preventScroll: true
        });
      });
      return () => {
        cancelAnimationFrame(frameId);
      };
    };
    t8 = [isKeyboardActiveItem, item.id, keyboardOriginIndex, onKeyboardStateChange, productInfos.length];
    $[16] = isKeyboardActiveItem;
    $[17] = item.id;
    $[18] = keyboardOriginIndex;
    $[19] = onKeyboardStateChange;
    $[20] = productInfos.length;
    $[21] = t7;
    $[22] = t8;
  } else {
    t7 = $[21];
    t8 = $[22];
  }
  useEffect(t7, t8);
  let t9;
  if ($[23] !== activeProductInfo || $[24] !== cardPosition || $[25] !== handleProductCardClick || $[26] !== isCardOpen || $[27] !== isCardVisible || $[28] !== item || $[29] !== setDataLayerProductInMainClick) {
    t9 = cardPosition != null && isCardVisible && typeof document !== "undefined" ? createPortal(<CSSTransition in={isCardOpen} appear={true} timeout={POPUP_SCALE_TRANSITION_MS} classNames={{
      appear: styles.homePopupScaleAppear,
      appearActive: styles.homePopupScaleAppearActive,
      enter: styles.homePopupScaleEnter,
      enterActive: styles.homePopupScaleEnterActive,
      exit: styles.homePopupScaleExit,
      exitActive: styles.homePopupScaleExitActive
    }} nodeRef={popupNodeRef} mountOnEnter={true} unmountOnExit={true}><div ref={popupNodeRef} aria-hidden={true} className={styles.homeItemCardPopup} style={{
        position: "fixed",
        left: `${cardPosition.x}px`,
        top: `${cardPosition.y}px`,
        "--home-card-width": `${HERO_CARD_LARGE_SIZE.width}px`,
        "--home-card-height": `${HERO_CARD_LARGE_SIZE.height}px`
      } as React.CSSProperties & Record<string, string>}><button type="button" tabIndex={-1} aria-label={activeProductInfo?.name ? `Открыть ${activeProductInfo.name}` : "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u043A\u0430\u0440\u0442\u043E\u0447\u043A\u0443 \u0442\u043E\u0432\u0430\u0440\u0430"} className={styles.homeItemStretched} onClick={() => {
          setDataLayerProductInMainClick(item);
          handleProductCardClick();
        }} /><HeroCard size="large" product={{
          image: activeProductInfo?.cardImage ?? HERO_CARD_IMAGE,
          name: activeProductInfo?.name ?? "",
          formattedPrice: activeProductInfo?.formattedPrice ?? "0 \u20BD",
          oldPrice: activeProductInfo?.oldPrice,
          discountPercent: activeProductInfo?.discountPercent,
          url: undefined,
          isPresave: activeProductInfo?.isPresave
        }} /></div></CSSTransition>, document.body) : null;
    $[23] = activeProductInfo;
    $[24] = cardPosition;
    $[25] = handleProductCardClick;
    $[26] = isCardOpen;
    $[27] = isCardVisible;
    $[28] = item;
    $[29] = setDataLayerProductInMainClick;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const popup = t9;
  const t10 = activeProductInfo?.name ?? "";
  let t11;
  if ($[31] !== isPriorityImage || $[32] !== item.id || $[33] !== item.image || $[34] !== size.height || $[35] !== size.width || $[36] !== t10) {
    t11 = <NextImage className={styles.homeImage} data-home-item-id={item.id} src={item.image} alt={t10} priority={isPriorityImage} width={size.width} height={size.height} draggable={false} />;
    $[31] = isPriorityImage;
    $[32] = item.id;
    $[33] = item.image;
    $[34] = size.height;
    $[35] = size.width;
    $[36] = t10;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const content = t11;
  if (productInfos.length > 0) {
    const t12 = isCenterZoomed ? "true" : undefined;
    let t13;
    if ($[38] !== activeOriginIndex || $[39] !== baseSize || $[40] !== breakpoint || $[41] !== handleTriggerClick || $[42] !== handleTriggerEnter || $[43] !== handleTriggerHover || $[44] !== isCardOpen || $[45] !== isKeyboardActiveItem || $[46] !== item || $[47] !== keyboardOriginIndex || $[48] !== onKeyboardNavigateItem || $[49] !== onKeyboardStateChange || $[50] !== productInfos || $[51] !== size.height || $[52] !== size.width) {
      t13 = productInfos.map((productInfo, originIndex) => {
        const originPoint = resolveResponsivePoint(productInfo.origin, breakpoint);
        const {
          x,
          y
        } = originPoint;
        const shouldAutoPosition = productInfo.autoPosition !== false;
        const originX = shouldAutoPosition ? x * size.width / baseSize.width : x;
        const originY = shouldAutoPosition ? y * size.height / baseSize.height : y;
        return <div key={`${item.id}-${productInfo.slug ?? productInfo.name}-${originIndex}`} className={styles.homeItemTrigger} style={{
          "--home-trigger-left": `${originX}px`,
          "--home-trigger-top": `${originY}px`
        } as React.CSSProperties & Record<string, string>}><ModalTriggerButton ref={node => {
            triggerRefs.current[originIndex] = node;
          }} tabIndex={isKeyboardActiveItem && keyboardOriginIndex === originIndex ? 0 : -1} isActive={isCardOpen && activeOriginIndex === originIndex} onFocus={() => {
            onKeyboardStateChange(item.id, originIndex);
          }} onClick={e => {
            e.stopPropagation();
            onKeyboardStateChange(item.id, originIndex);
            handleTriggerClick(productInfo, originIndex, e.currentTarget.getBoundingClientRect());
          }} onMouseEnter={e_0 => {
            handleTriggerHover(e_0, productInfo, originIndex);
          }} onKeyDown={e_1 => {
            if (e_1.key === "Tab") {
              e_1.preventDefault();
              e_1.stopPropagation();
              if (productInfos.length <= 1) {
                onKeyboardStateChange(item.id, originIndex);
                triggerRefs.current[originIndex]?.focus({
                  preventScroll: true
                });
                return;
              }
              const direction = e_1.shiftKey ? -1 : 1;
              const nextIndex = (originIndex + direction + productInfos.length) % productInfos.length;
              onKeyboardStateChange(item.id, nextIndex);
              onKeyboardNavigateItem(item, nextIndex);
              requestAnimationFrame(() => {
                triggerRefs.current[nextIndex]?.focus({
                  preventScroll: true
                });
              });
              return;
            }
            if (e_1.key !== "Enter" && e_1.key !== " ") {
              return;
            }
            e_1.preventDefault();
            e_1.stopPropagation();
            onKeyboardStateChange(item.id, originIndex);
            handleTriggerEnter(productInfo, originIndex, e_1.currentTarget.getBoundingClientRect());
          }} /></div>;
      });
      $[38] = activeOriginIndex;
      $[39] = baseSize;
      $[40] = breakpoint;
      $[41] = handleTriggerClick;
      $[42] = handleTriggerEnter;
      $[43] = handleTriggerHover;
      $[44] = isCardOpen;
      $[45] = isKeyboardActiveItem;
      $[46] = item;
      $[47] = keyboardOriginIndex;
      $[48] = onKeyboardNavigateItem;
      $[49] = onKeyboardStateChange;
      $[50] = productInfos;
      $[51] = size.height;
      $[52] = size.width;
      $[53] = t13;
    } else {
      t13 = $[53];
    }
    let t14;
    if ($[54] !== content || $[55] !== popup || $[56] !== t12 || $[57] !== t13 || $[58] !== wrapperStyle) {
      t14 = <div className={styles.homeItemWrapper} style={wrapperStyle} data-home-center-zoom={t12}>{content}{t13}{popup}</div>;
      $[54] = content;
      $[55] = popup;
      $[56] = t12;
      $[57] = t13;
      $[58] = wrapperStyle;
      $[59] = t14;
    } else {
      t14 = $[59];
    }
    return t14;
  }
  const t12 = isCenterZoomed ? "true" : undefined;
  let t13;
  if ($[60] !== content || $[61] !== t12 || $[62] !== wrapperStyle) {
    t13 = <div className={styles.homeItemWrapper} style={wrapperStyle} data-home-center-zoom={t12}>{content}</div>;
    $[60] = content;
    $[61] = t12;
    $[62] = wrapperStyle;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  return t13;
};
export default HomeItemBlock;